import { Check, Palette } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const PALETTES = [
  {
    id: "green",
    label: "Evergreen",
    light: "160 84% 28%",
    dark: "160 70% 40%",
    lightForeground: "0 0% 100%",
    darkForeground: "220 25% 9%",
  },
  {
    id: "sky",
    label: "Azure blue",
    light: "207 90% 50%",
    dark: "199 95% 58%",
    lightForeground: "220 25% 9%",
    darkForeground: "220 25% 9%",
  },
  {
    id: "cyan",
    label: "Bright cyan",
    light: "198 92% 52%",
    dark: "190 100% 60%",
    lightForeground: "220 25% 9%",
    darkForeground: "220 25% 9%",
  },
  {
    id: "lime",
    label: "Lime green",
    light: "132 88% 43%",
    dark: "122 100% 55%",
    lightForeground: "220 25% 9%",
    darkForeground: "220 25% 9%",
  },
  {
    id: "violet",
    label: "Vivid violet",
    light: "263 82% 48%",
    dark: "264 96% 66%",
    lightForeground: "0 0% 100%",
    darkForeground: "220 25% 9%",
  },
  {
    id: "ocean",
    label: "Ocean",
    light: "203 89% 34%",
    dark: "199 80% 48%",
    lightForeground: "0 0% 100%",
    darkForeground: "220 25% 9%",
  },
  {
    id: "berry",
    label: "Berry",
    light: "335 76% 37%",
    dark: "335 78% 58%",
    lightForeground: "0 0% 100%",
    darkForeground: "220 25% 9%",
  },
  {
    id: "amber",
    label: "Amber",
    light: "32 92% 36%",
    dark: "38 92% 50%",
    lightForeground: "220 25% 9%",
    darkForeground: "220 25% 9%",
  },
  {
    id: "indigo",
    label: "Indigo",
    light: "230 70% 42%",
    dark: "230 85% 66%",
    lightForeground: "0 0% 100%",
    darkForeground: "220 25% 9%",
  },
];

const DEFAULT_ACCENT = "green";

const paletteFor = (accent) =>
  PALETTES.find((palette) => palette.id === accent) || PALETTES[0];

const applyAccent = (accent, dark) => {
  const palette = paletteFor(accent);
  const colour = dark ? palette.dark : palette.light;
  const root = document.documentElement;
  root.style.setProperty("--primary", colour);
  root.style.setProperty(
    "--primary-foreground",
    dark ? palette.darkForeground : palette.lightForeground,
  );
  root.style.setProperty("--ring", colour);
  root.style.setProperty("--accent", colour);
  root.style.setProperty(
    "--accent-foreground",
    dark ? palette.darkForeground : palette.lightForeground,
  );
};

export const AccentPicker = ({ dark }) => {
  const { user } = useAuth();
  const [accent, setAccent] = useState(DEFAULT_ACCENT);

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    api
      .get("/me/preferences")
      .then(({ data }) => {
        if (active) setAccent(data.accent || DEFAULT_ACCENT);
      })
      .catch(() => {
        if (active) setAccent(DEFAULT_ACCENT);
      });
    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    applyAccent(accent, dark);
  }, [accent, dark]);

  const chooseAccent = async (nextAccent) => {
    const previousAccent = accent;
    setAccent(nextAccent);
    try {
      await api.put("/me/preferences", { accent: nextAccent });
      toast.success(`${paletteFor(nextAccent).label} accent saved`);
    } catch (error) {
      setAccent(previousAccent);
      toast.error(errMsg(error));
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          aria-label="Choose accent colour"
          size="icon"
          title="Choose accent colour"
          variant="ghost"
          data-testid="accent-picker-button"
        >
          <Palette className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-3" data-testid="accent-picker-panel">
        <p className="mb-2 text-sm font-semibold" data-testid="accent-picker-title">
          Accent colour
        </p>
        <div className="grid gap-1" data-testid="accent-picker-options">
          {PALETTES.map((palette) => {
            const selected = accent === palette.id;
            const colour = dark ? palette.dark : palette.light;
            return (
              <Button
                key={palette.id}
                aria-pressed={selected}
                className="justify-start gap-3"
                onClick={() => chooseAccent(palette.id)}
                size="sm"
                type="button"
                variant={selected ? "secondary" : "ghost"}
                data-testid={`accent-option-${palette.id}`}
              >
                <span
                  aria-hidden="true"
                  className="h-4 w-4 rounded-full ring-1 ring-black/15"
                  style={{ backgroundColor: `hsl(${colour})` }}
                />
                <span className="flex-1 text-left">{palette.label}</span>
                {selected && <Check className="h-4 w-4" data-testid="accent-selected-check" />}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};